import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { StoreContext } from '../../context/StoreContext';
import './Checkout.css';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { toast } from 'react-toastify';
import {
    placeOrderOnline,
    placeRazorpayOrder,
    verifyRazorpayPayment,
    calculateCartTotals
} from '../../api/apiService';
import { formatPrice } from '../../utils/formatPrice';

// --- MAP CONFIGURATION ---
const LIBRARIES = ['places'];
const MAP_CONTAINER_STYLE = { width: '100%', height: '350px', marginTop: '15px', borderRadius: '8px' };
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 }; // India Center

const Checkout = () => {
    const {
        cart,
        currency,
        user,
        clearUserCart,
        token,
        guestVerified,
        guestEmail,
        guestPhone,
        vendor_names,
        authLoading
    } = useContext(StoreContext);

    const navigate = useNavigate();

    // --- STATE MANAGEMENT ---
    const [isDifferentAddress, setIsDifferentAddress] = useState(false);
    const [addressData, setAddressData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        door_number: '',
        street_name: '',
        zip_code: '',
        city: '',
        state: '',
        country: '',
        phone_number: '',
        address_id: null,
        latitude: null,
        longitude: null,
        locality: ''
    });

    const [paymentMode, setPaymentMode] = useState('stripe');
    const [isPlacingOrder, setIsPlacingOrder] = useState(false);
    const [orderError, setOrderError] = useState(null);
    const [deliveryPreference, setDeliveryPreference] = useState('standard');
    const [tip, setTip] = useState(0); // Added tip state based on patch logic
    const [isDeliverable, setIsDeliverable] = useState(true);

      // ✅ NEW STATE
    const [showNextDayModal, setShowNextDayModal] = useState(false);
    const [nextShiftTime, setNextShiftTime] = useState(null); // Stores backend target time


    // Map & Location State
    const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
    const [markerPosition, setMarkerPosition] = useState(null);
    const [showMap, setShowMap] = useState(false);
    // const [searchBox, setSearchBox] = useState(null);

    // Refs & Loaders
    const addressInputRef = useRef(null);
    const isPlaceSelectedRef = useRef(false); // Prevents blur from overwriting selection
    const [geocoderService, setGeocoderService] = useState(null);
    const [isGoogleMapsLibLoaded, setIsGoogleMapsLibLoaded] = useState(false);
    const [isRazorpayLibLoaded, setIsRazorpayLibLoaded] = useState(false);

    // Bill Details State
    const [billDetails, setBillDetails] = useState({
        subtotal: 0,
        totalDiscount: 0,
        taxAmount: 0,
        taxes:0,
        platformFee: 0,
        deliveryCharge: 0,
        netAmount: 0
    });
    const [isCalculating, setIsCalculating] = useState(false);

    // Load Google Maps API
    const { isLoaded: isMapLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_Maps_API_KEY,
        libraries: LIBRARIES
    });

    // --- EFFECTS ---

    // 1. Check for Libraries
    useEffect(() => {
        if (isMapLoaded && window.google && window.google.maps) {
            setIsGoogleMapsLibLoaded(true);
            if (window.google.maps.Geocoder) {
                setGeocoderService(new window.google.maps.Geocoder());
            } else {
                console.warn("Google Maps Geocoder library not available. Address geocoding may not work properly.");
            }
        }
    }, [isMapLoaded]);

    useEffect(() => {
        const checkRazorpayInterval = setInterval(() => {
            if (window.Razorpay) {
                clearInterval(checkRazorpayInterval);
                setIsRazorpayLibLoaded(true);
            }
        }, 100);
        return () => clearInterval(checkRazorpayInterval);
    }, []);

    // 2. Autofill User Data
    useEffect(() => {
        if (isDifferentAddress) return;

        setAddressData(prev => ({
            ...prev,
            firstName: user?.firstName || prev.firstName || '',
            lastName: user?.lastName || prev.lastName || '',
            email: user?.email || guestEmail || prev.email || '',
            door_number: user?.doorNumber || prev.door_number || '',
            street_name: user?.streetName || prev.street_name || '',
            zip_code: user?.zipCode || prev.zip_code || '',
            city: user?.city || prev.city || '',
            state: user?.state || prev.state || '',
            country: user?.country || prev.country || '',
            phone_number: user?.phone || guestPhone || prev.phone_number || '',
            latitude: (user?.location?.coordinates?.[1] && prev.latitude === null) ? user.location.coordinates[1] : prev.latitude,
            longitude: (user?.location?.coordinates?.[0] && prev.longitude === null) ? user.location.coordinates[0] : prev.longitude,
            locality: user?.locality || prev.locality || '',
        }));
    }, [user, guestVerified, guestEmail, guestPhone, navigate, isDifferentAddress]);

    // 3. Fetch Totals (Merged Logic)
    useEffect(() => {
        const fetchTotals = async () => {
            if (cart?._id && cart.items.length > 0) {
                setIsCalculating(true);
                try {
                    const payload = {
                        cart_id: cart._id,
                        tip: tip // Added tip to payload
                    };
                    
                    // Use marker position if available, otherwise address data lat/lng
                    if (markerPosition) {
                        payload.userLocation = {
                            lat: markerPosition.lat,
                            lng: markerPosition.lng
                        };
                    } else if (addressData.latitude && addressData.longitude) {
                        payload.userLocation = {
                            lat: addressData.latitude,
                            lng: addressData.longitude
                        };
                    }

                    const response = await calculateCartTotals(payload);
                    if (response.success) {
                        // CHECK FOR OUT OF RANGE (999 or user defined limit)
                        if (response.totals.deliveryCharge >= 999) {
                            setIsDeliverable(false);
                            // Hide the ugly 999 from the user, set to 0 for display
                            setBillDetails({ ...response.totals, deliveryCharge: 0, netAmount: 0 });
                        } else {
                            setIsDeliverable(true);
                            setBillDetails(response.totals);
                        }
                    }
                } catch (error) {
                    console.error("Failed to calculate cart totals:", error);
                    setBillDetails(prev => ({ ...prev,taxes: 0, deliveryCharge: 0, netAmount: 0 })); // Reset critical fields on error
                } finally {
                    setIsCalculating(false);
                }
            } else {
                setBillDetails({
                    subtotal: 0,
                    totalDiscount: 0,
                    taxAmount: 0,
                    taxes:0,
                    platformFee: 0,
                    deliveryCharge: 0,
                    netAmount: 0
                });
            }
        };

        // Debounce the call
        const timer = setTimeout(() => {
            fetchTotals();
        }, 500);

        return () => clearTimeout(timer);
    }, [cart, addressData.latitude, addressData.longitude, markerPosition, tip]);


    // --- MAP HELPER FUNCTIONS (Moved inside component) ---

    const reverseGeocode = (pos) => {
        if (!window.google) return;
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: pos }, (results, status) => {
            if (status === "OK" && results[0]) {
                const components = results[0].address_components;
                let city = "", state = "", country = "", zipcode = "", locality = "", street = "";

                components.forEach(comp => {
                    if (comp.types.includes("locality")) city = comp.long_name;
                    if (comp.types.includes("administrative_area_level_1")) state = comp.long_name;
                    if (comp.types.includes("country")) country = comp.long_name;
                    if (comp.types.includes("postal_code")) zipcode = comp.long_name;
                    if (comp.types.includes("sublocality_level_1")) locality = comp.long_name;
                    if (comp.types.includes("route")) street = comp.long_name;
                });

                // Update address form with geocoded data
                setAddressData(prev => ({
                    ...prev,
                    city, state, country, zip_code: zipcode, locality,
                    street_name: street || prev.street_name, // Optional: auto-fill street if found
                    latitude: pos.lat,
                    longitude: pos.lng
                }));
                
                // Update input ref if needed
                if (addressInputRef.current && street) {
                    addressInputRef.current.value = street;
                }
            }
        });
    };

    const detectLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const pos = { lat: position.coords.latitude, lng: position.coords.longitude };
                    setMapCenter(pos);
                    setMarkerPosition(pos);
                    setShowMap(true);
                    reverseGeocode(pos);
                },
                () => toast.error("Location access denied. Please search manually.")
            );
        } else {
            toast.error("Geolocation is not supported by this browser.");
        }
    };

    const onMarkerDragEnd = (e) => {
        const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        setMarkerPosition(pos);
        reverseGeocode(pos);
    };


    // --- HANDLERS ---

    const vendorName = (cart?.vendor_id && vendor_names?.[cart.vendor_id])
            ? vendor_names[cart.vendor_id]
            : localStorage.getItem('vendor_name') || 'Vendor';

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setAddressData(prev => ({ ...prev, [name]: value }));
        // Reset lat/lng if manual edits occur on critical fields (avoids mismatch)
        if (['street_name', 'zip_code', 'city', 'state', 'country'].includes(name)) {
            // Note: We don't reset immediately if we just set it via map, 
            // but this logic assumes typing means "new address".
            // To be safe, we rely on geocoding/map for final lat/lng.
        }
    };

    const handleDifferentAddressToggle = (e) => {
        const isChecked = e.target.checked;
        setIsDifferentAddress(isChecked);

        if (isChecked) {
            setAddressData({
                firstName: '', lastName: '', email: '', door_number: '', street_name: '',
                zip_code: '', city: '', state: '', country: '', phone_number: '',
                address_id: null, latitude: null, longitude: null, locality: ''
            });
            setMarkerPosition(null);
            if (addressInputRef.current) addressInputRef.current.value = '';
        } else {
            // Restore from user
            setAddressData({
                firstName: user?.firstName || '',
                lastName: user?.lastName || '',
                email: user?.email || guestEmail || '',
                door_number: user?.doorNumber || '',
                street_name: user?.streetName || '',
                zip_code: user?.zipCode || '',
                city: user?.city || '',
                state: user?.state || '',
                country: user?.country || '',
                phone_number: user?.phone || '',
                address_id: null,
                latitude: user?.location?.coordinates?.[1] || null,
                longitude: user?.location?.coordinates?.[0] || null,
                locality: user?.locality || '',
            });
            if (user?.location?.coordinates) {
                setMarkerPosition({ 
                    lat: user.location.coordinates[1], 
                    lng: user.location.coordinates[0] 
                });
            }
            if (addressInputRef.current) addressInputRef.current.value = user?.streetName || '';
        }
    };

    // --- PATCH START: Re-ordered Map Handlers ---

    // 1. Define GEOCODE HELPER FIRST (Independent)
    const handleGeocodeAddress = useCallback(async (overrideText = null) => {
        if (!isGoogleMapsLibLoaded || !geocoderService) {
            console.error("Geocoder service is not available.");
            return [null, null];
        }

        const fullAddress = overrideText || [
            addressData.door_number, 
            addressData.street_name, 
            addressData.city, 
            addressData.state, 
            addressData.zip_code, 
            addressData.country
        ].filter(Boolean).join(", ");

        if (!fullAddress.trim()) return [null, null];

        return new Promise((resolve) => {
            geocoderService.geocode({ address: fullAddress }, (results, status) => {
                if (status === "OK" && results && results[0]) {
                    const loc = results[0].geometry.location;
                    const lat = loc.lat();
                    const lng = loc.lng();
                    resolve([lng, lat]);
                } else {
                    console.error(`Geocode failed: ${status}`);
                    resolve([null, null]);
                }
            });
        });
    }, [addressData, isGoogleMapsLibLoaded, geocoderService]);

    // 2. Define BLUR HANDLER SECOND (Uses Geocode Helper)
    const handleStreetBlur = useCallback(async () => {
        if (isPlaceSelectedRef.current) {
            isPlaceSelectedRef.current = false; 
            return;
        }

        if (addressData.street_name && (addressData.latitude === null || addressData.longitude === null)) {
            const [lng, lat] = await handleGeocodeAddress();
            if (lng && lat) {
                setMarkerPosition({ lat, lng });
                setMapCenter({ lat, lng });
                setShowMap(true);
                setAddressData(prev => ({ ...prev, latitude: lat, longitude: lng }));
            }
        }
    }, [addressData.street_name, addressData.latitude, addressData.longitude, handleGeocodeAddress]);

    // Note: Removed deprecated Autocomplete component
    // User can manually enter address and it will be geocoded on blur/Enter

    // --- PATCH END ---

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault(); // Stop form submission
            handleStreetBlur(); // Trigger manual geocode to sync map
        }
    };

    // ✅ NEW FUNCTION: Handle Next Day Confirmation
    const handleConfirmNextDay = () => {
        setShowNextDayModal(false);
        // Call placeOrderHandler again, but pass true for retry
        placeOrderHandler(null, true);
    };

   const placeOrderHandler = async (e, acceptSchedule = false) => {
        if (e) e.preventDefault();

        setIsPlacingOrder(true);
        setOrderError(null);

        if (!cart.vendor_id) {
            setOrderError('No vendor associated with your cart. Please add items from a vendor.');
            setIsPlacingOrder(false);
            return;
        }

        // Logic to determine final location:
        // 1. Use addressData lat/lng (populated by Map pin or Autocomplete)
        // 2. If missing, try geocoding the text fields
        let finalLat = addressData.latitude;
        let finalLng = addressData.longitude;

        if (finalLat === null || finalLng === null) {
            const [returnedLng, returnedLat] = await handleGeocodeAddress();
            finalLng = returnedLng;
            finalLat = returnedLat;
            
            if (finalLat === null || finalLng === null) {
                setOrderError('Delivery location is missing. Please pin your location on the map or ensure address is correct.');
                setIsPlacingOrder(false);
                return;
            }
        }

        const fullName = `${addressData.firstName} ${addressData.lastName}`.trim();
        const requiredFields = ['firstName', 'email', 'door_number', 'street_name', 'city', 'state', 'zip_code', 'country', 'phone_number'];
        const missingFields = requiredFields.filter(field => !addressData[field] || addressData[field].toString().trim() === '');

        if (missingFields.length > 0) {
            setOrderError(`Please fill in all required fields: ${missingFields.join(', ')}.`);
            setIsPlacingOrder(false);
            return;
        }

        const deliveryAddressPayload = {
            name: fullName,
            email: addressData.email,
            door_number: addressData.door_number,
            street_name: addressData.street_name,
            street: addressData.street_name,
            locality: addressData.locality || "",
            city: addressData.city,
            state: addressData.state,
            country: addressData.country,
            zip_code: addressData.zip_code,
            phone_number: addressData.phone_number,
            location: {
                type: 'Point',
                coordinates: [finalLng, finalLat]
            },
            address_id: addressData.address_id || null
        };

        // Construct Payload (Same as before)
        const currentNetAmount = parseFloat(billDetails.netAmount.toFixed(2));
        let requestPayload = {
            cart_id: cart._id,
            amount: currentNetAmount,
            vendorName: vendorName,
            deliveryAddress: deliveryAddressPayload,
            payment_mode: paymentMode,
            tip: tip,
            acceptSchedule: acceptSchedule // ✅ Send updated flag to backend
        };

        if (user && user._id && token) {
            requestPayload.user_id = user._id;
            localStorage.removeItem('guest_id');
            localStorage.removeItem('guest_verified');
            localStorage.removeItem('guest_email');
        } else if (guestVerified && (guestPhone || guestEmail || addressData.email)) {
            requestPayload.guest_id = localStorage.getItem('guest_id');
            requestPayload.guestEmail = addressData.email || null;
            requestPayload.guestPhone = guestPhone || null;
        } else {
            setOrderError('Login or guest verification is required to place an order.');
            setIsPlacingOrder(false);
            return;
        }

        try {
            if (paymentMode === 'stripe') {
                const response = await placeOrderOnline({
                    ...requestPayload,
                    payment_mode: 'Stripe'
                });
                if (response.data.success && response.data.session_url) {
                    window.location.href = response.data.session_url;
                } else {
                    throw new Error(response.data.message || 'Stripe payment initiation failed.');
                }
            } else if (paymentMode === 'razorpay') {
                if (!isRazorpayLibLoaded) throw new Error('Razorpay script not loaded.');
                
                const response = await placeRazorpayOrder({
                    ...requestPayload,
                    payment_mode: 'Razorpay'
                });
                
                if (response.data.success && response.data.razorpayOrderId) {
                    const { orderId, razorpayOrderId, amount, currency, key_id } = response.data;
                    const options = {
                        key: key_id,
                        amount: amount,
                        currency: currency,
                        name: "Food Delivery App",
                        webview_intent: true,
                        description: `Order ID: ${orderId.substring(orderId.length - 6)}`,
                        order_id: razorpayOrderId,
                        handler: async function (razorpayResponse) {
                            try {
                                const verifyRes = await verifyRazorpayPayment({
                                    ...razorpayResponse,
                                    orderId: orderId
                                });
                                if (verifyRes.data.success) {
                                    await clearUserCart();
                                    navigate(`/order-confirmation?orderId=${orderId}&payment=razorpay&success=true`);
                                } else {
                                    navigate(`/order-confirmation?orderId=${orderId}&payment=razorpay&success=false`);
                                }
                            } catch (error) {
                                navigate(`/order-confirmation?orderId=${orderId}&payment=razorpay&success=false`);
                            }
                        },
                        prefill: {
                            name: deliveryAddressPayload.name,
                            email: addressData.email,
                            contact: addressData.phone_number
                        },
                        theme: { color: "#F37254" }
                    };
                    const rzp = new window.Razorpay(options);
                    // rzp.on('payment.failed', function () {
                    //     navigate(`/order-confirmation?orderId=${orderId}&payment=razorpay&success=false`);
                    // });
                    // rzp.open();
                    rzp.on('payment.failed', function (response) {

                     console.log("RAZORPAY FAILED:", response);

                    navigate(`/order-confirmation?orderId=${orderId}&payment=razorpay&success=false`);
                    });

                    setTimeout(() => {
                    rzp.open();
                    }, 500);
                } else {
                    throw new Error(response.data.message || 'Razorpay initiation failed.');
                }
            }
         } catch (error) {
            // ✅ CHECK FOR SCHEDULE CONFIRMATION
            if (error.response && error.response.data && error.response.data.requiresScheduleConfirmation) {
                setNextShiftTime(error.response.data.nextShiftStart);
                setShowNextDayModal(true);
            } else {

                setOrderError(error.message || 'An error occurred while placing order.');
            }
        } finally {
            // Only stop loading if we aren't showing the modal (UX choice)
            setIsPlacingOrder(false);
        }
    };

    // --- RENDER ---

    if (authLoading) {
        return (
            <div className="checkout-page loading-state">
                <div className="loading-spinner"></div>
                <p>Finalizing your session, please wait...</p>
            </div>
        );
    }

    if (cart.items.length === 0) {
        return (
            <div className="checkout-page empty-cart-state">
                <h2>Your Cart is Empty</h2>
                <p>Add some items to proceed to checkout!</p>
                <button onClick={() => navigate('/')} className="empty-cart-button">Browse Menu</button>
            </div>
        );
    }

    const displayTotal = billDetails.netAmount || 0;

    return (
        <div className="checkout-page">
            <form onSubmit={placeOrderHandler} className="checkout-form">
                <div className="checkout-main-content">
                    {/* LEFT COLUMN */}
                    <div className="checkout-left-column">
                        <div className="checkout-section">
                            <h2 className="section-title">1. Delivery Information</h2>
                            
                            {/* MAP INTEGRATION */}
                            <div className="map-section" style={{ marginBottom: '20px' }}>
                                <button type="button" className="btn-detect-location" onClick={detectLocation} style={{ marginBottom: '10px' }}>
                                    📍 Detect My Current Location
                                </button>

                                {isMapLoaded && (showMap || markerPosition) && (
                                    <div className="map-container-wrapper">
                                        <GoogleMap
                                            mapContainerStyle={MAP_CONTAINER_STYLE}
                                            center={mapCenter}
                                            zoom={15}
                                            onClick={(e) => {
                                                const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
                                                setMarkerPosition(pos);
                                                reverseGeocode(pos);
                                            }}
                                        >
                                            {markerPosition && (
                                                <Marker position={markerPosition} draggable={true} onDragEnd={onMarkerDragEnd} />
                                            )}
                                        </GoogleMap>
                                        <p className="map-helper-text" style={{ fontSize: '0.85rem', color: '#666', marginTop: '5px' }}>
                                            Drag the pin to your exact building entrance.
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="input-row">
                                <input 
                                    type="text" 
                                    name="firstName" 
                                    placeholder={isDifferentAddress ? "First Name *" : "First Name *"} 
                                    value={addressData.firstName} 
                                    onChange={handleAddressChange} 
                                    required 
                                />
                                <input 
                                    type="text" 
                                    name="lastName" 
                                    placeholder={isDifferentAddress ? "Last Name *" : "Last Name *"} 
                                    value={addressData.lastName} 
                                    onChange={handleAddressChange} 
                                    required 
                                />
                            </div>
                            <input 
                                type="email" 
                                name="email" 
                                placeholder={isDifferentAddress ? "Email *" : "Email Address *"} 
                                value={addressData.email} 
                                onChange={handleAddressChange} 
                                required 
                                // Logic: Read-only ONLY if logged in AND using default address. 
                                // If 'Deliver to different address' is checked, this becomes editable.
                                readOnly={!!user && !isDifferentAddress} 
                                style={isDifferentAddress ? { borderColor: '#27ae60', backgroundColor: '#fafffb' } : {}}
                            />
                            
                            {isGoogleMapsLibLoaded ? (
                                <input
                                    type="text"
                                    name="street_name"
                                    placeholder="Enter your street address or landmark *"
                                    onBlur={handleStreetBlur}
                                    onKeyDown={handleKeyDown}
                                    required
                                    ref={addressInputRef}
                                    value={addressData.street_name}
                                    onChange={handleAddressChange}
                                />
                            ) : <div>Loading Map Services...</div>}
                            
                            <input type="text" name="door_number" placeholder="Door/Flat Number *" value={addressData.door_number} onChange={handleAddressChange} required />
                            <div className="input-row">
                                <input type="text" name="city" placeholder="City *" value={addressData.city} onChange={handleAddressChange} required />
                                <input type="text" name="state" placeholder="State *" value={addressData.state} onChange={handleAddressChange} required />
                                <input type="text" name="zip_code" placeholder="Zip Code *" value={addressData.zip_code} onChange={handleAddressChange} required />
                            </div>
                            <input type="text" name="country" placeholder="Country *" value={addressData.country} onChange={handleAddressChange} required />
                            <input type="text" name="phone_number" placeholder="Phone Number *" value={addressData.phone_number} onChange={handleAddressChange} required />

                            {/* Delivery Preference */}
                            <div className="delivery-pref-section">
                                <div className="delivery-pref-title">Delivery Preference</div>
                                <div className="delivery-pref-options">
                                    <label className={`delivery-pref-option${deliveryPreference === 'standard' ? ' selected' : ''}`}>
                                        <input type="radio" name="deliveryPreference" value="standard" checked={deliveryPreference === 'standard'} onChange={() => setDeliveryPreference('standard')} />
                                        <div>
                                            <div className="delivery-pref-main">Standard Delivery</div>
                                            <div className="delivery-pref-desc">Handed directly to you.</div>
                                        </div>
                                    </label>
                                    <label className={`delivery-pref-option${deliveryPreference === 'contactless' ? ' selected' : ''}`}>
                                        <input type="radio" name="deliveryPreference" value="contactless" checked={deliveryPreference === 'contactless'} onChange={() => setDeliveryPreference('contactless')} />
                                        <div>
                                            <div className="delivery-pref-main">Contactless Delivery</div>
                                            <div className="delivery-pref-desc">Left at doorstep/safe place.</div>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {user && (
                                <div className="different-address-toggle">
                                    <label>
                                        <input type="checkbox" checked={isDifferentAddress} onChange={handleDifferentAddressToggle} />
                                        Deliver to different/other address
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT COLUMN */}
                    <div className="checkout-right-column">
                        <div className="checkout-section order-summary-section">
                            <h2 className="section-title">3. Order Summary</h2>
                            <div className="cart-items-list">
                                {cart.items.map((item) => (
                                    <div key={item._id} className="summary-item">
                                        <img src={item.image?.startsWith('http') ? item.image : `/images/${item.image}`} alt={item.name} className="summary-item-image" />
                                        <div className="summary-item-details">
                                            <div className="summary-item-name">{item.name} <span className="summary-item-quantity">x {item.quantity}</span></div>
                                            <div className="summary-item-vendor">
                                                ({cart.vendor_type === 'restaurant' ? 'restaurant' : cart.vendor_type === 'grocery_store' ? 'grocery store' : 'cloud kitchen'})
                                            </div>
                                            {item.spicyLevel && item.spicyLevel.trim() && (
                                                <div className="summary-item-customization">🌶️ Spicy: <span>{item.spicyLevel}</span></div>
                                            )}
                                        </div>
                                        <div className="summary-item-price">{formatPrice((item.price_per_unit * item.quantity), cart.currency, currency)}</div>
                                    </div>
                                ))}
                            </div>
                            
                            {/* <div className="detail-row"><span>Subtotal:</span><span>{formatPrice(billDetails.subtotal, cart.currency, currency)}</span></div>
                            <div className="detail-row"><span>Discount:</span><span>- {formatPrice(billDetails.totalDiscount, cart.currency, currency)}</span></div>
                            
                            {(cart?.currency === 'USD' || cart?.currency === 'CAD') && (
                                <div className="detail-row"><span>Estimated Tax:</span><span>{formatPrice(0, cart.currency, currency)}</span></div>
                            )}
                            
                            {billDetails.taxAmount > 0 && (
                                <div className="detail-row"><span>Total GST:</span><span>{formatPrice(billDetails.taxAmount, cart.currency, currency)}</span></div>
                            )}
                            <div className="detail-row"><span>Platform Fee:</span>
                                <span>
                                    {billDetails.isFirstOrder ? (
                                        <>
                                            <span style={{ textDecoration: 'line-through', color: '#999', marginRight: '8px' }}>
                                                {formatPrice(15, cart.currency, currency)}
                                            </span>
                                            <span style={{ color: '#2ecc71', fontWeight: 'bold' }}>0.00</span>
                                        </>
                                    ) : (
                                        formatPrice(billDetails.platformFee, cart.currency, currency)
                                    )}
                                </span>
                            </div>
                           ```jsx
<div className="detail-row"><span>Delivery Fee:</span><span>
    {isDeliverable 
        ? formatPrice(billDetails.deliveryCharge, cart.currency, currency) 
        : <span style={{color: 'red'}}>Not Available</span>}
</span></div>

<div className="detail-row">
    <span>Delivery Tip:</span>
    <span>{formatPrice(tip, cart.currency, currency)}</span>
</div>

{billDetails.taxes > 0 && (
    <div className="detail-row">
        <span>Taxes:</span>
        <span>{formatPrice(billDetails.taxes, cart.currency, currency)}</span>
    </div>
)}

<div className="detail-row total">
```

                                <span>Grand Total:</span>
                                <span>{isCalculating ? 'Calculating...' : formatPrice(displayTotal, cart.currency, currency)}</span>
                            </div>*/}
                            <div className="detail-row">
    <span>Subtotal:</span>
    <span>
        {formatPrice(billDetails.subtotal, cart.currency, currency)}
    </span>
</div>

<div className="detail-row">
    <span>Discount:</span>
    <span>
        - {formatPrice(billDetails.totalDiscount, cart.currency, currency)}
    </span>
</div>

<div className="detail-row">
    <span>Platform Fee + GST:</span>
    <span>
        {billDetails.isFirstOrder ? (
            <>
                <span
                    style={{
                        textDecoration: 'line-through',
                        color: '#999',
                        marginRight: '8px'
                    }}
                >
                    {formatPrice(
                        15 + billDetails.taxAmount,
                        cart.currency,
                        currency
                    )}
                </span>

                <span
                    style={{
                        color: '#2ecc71',
                        fontWeight: 'bold'
                    }}
                >
                    {formatPrice(
                        0,
                        cart.currency,
                        currency
                    )}
                </span>
            </>
        ) : (
            formatPrice(
                billDetails.platformFee + billDetails.taxAmount,
                cart.currency,
                currency
            )
        )}
    </span>
</div>

<div className="detail-row">
    <span>Delivery Fee:</span>
    <span>
        {isDeliverable
            ? formatPrice(
                billDetails.deliveryCharge,
                cart.currency,
                currency
            )
            : <span style={{ color: 'red' }}>Not Available</span>
        }
    </span>
</div>

{isDeliverable &&
    billDetails.weatherSurge?.enabled &&
    billDetails.weatherSurge?.amount > 0 && (
        <div className="detail-row">
            <span>
                🌧️ Weather surcharge:
            </span>
            <span>
                {formatPrice(
                    billDetails.weatherSurge.amount,
                    cart.currency,
                    currency
                )}
            </span>
        </div>
    )
}

{isDeliverable &&
    billDetails.weatherSurge?.enabled &&
    billDetails.weatherSurge?.reason && (
        <div
            className="detail-row"
            style={{
                fontSize: '13px',
                color: '#666'
            }}
        >
            <span>
                Reason: {billDetails.weatherSurge.reason}
            </span>
        </div>
    )
}

{isDeliverable &&
    billDetails.rushHourCharge?.enabled &&
    billDetails.rushHourCharge?.amount > 0 && (
        <div className="detail-row">
            <span>
                ⚡ Rush Hour Charge:
            </span>
            <span>
                {formatPrice(
                    billDetails.rushHourCharge.amount,
                    cart.currency,
                    currency
                )}
            </span>
        </div>
    )
}

<div className="detail-row">
    <span>Delivery Tip:</span>
    <span>
        {formatPrice(tip, cart.currency, currency)}
    </span>
</div>


{billDetails.taxes > 0 && (
    <div className="detail-row">
        <span>Taxes:</span>
        <span>
            {formatPrice(
                billDetails.taxes,
                cart.currency,
                currency
            )}
        </span>
    </div>
)}

<div className="detail-row total">
    <span>Grand Total:</span>
    <span>
        {isCalculating
            ? 'Calculating...'
            : formatPrice(
                displayTotal,
                cart.currency,
                currency
            )
        }
    </span>
</div>
                        </div> 

                        {/* Payment Method */}
                        <div className="checkout-section payment-method-section">
                            <h2 className="section-title">2. Payment Method</h2>
                            <div className="payment-options">
                                {/* <label className={`payment-option-label${paymentMode === 'stripe' ? ' selected' : ''}`}>
                                    <input type="radio" name="payment" value="stripe" checked={paymentMode === 'stripe'} onChange={() => setPaymentMode('stripe')} disabled={!isGoogleMapsLibLoaded} />
                                    Online Payment (via Card / Stripe)
                                </label> */}
                                <label className={`payment-option-label${paymentMode === 'razorpay' ? ' selected' : ''}`}>
                                    <input type="radio" name="payment" value="razorpay" checked={paymentMode === 'razorpay'} onChange={() => setPaymentMode('razorpay')} disabled={!isRazorpayLibLoaded} />
                                    Online Payment (via UPI / Netbanking / Razorpay)
                                </label>
                            </div>
                        </div>

                        {/* Place Order Button */}
                        <div className="sticky-place-order">
                            {/* Priority Error for Location */}
                            {!isDeliverable && (
                                <p className="checkout-error-message" style={{ marginBottom: '10px' }}>
                                    🚫 Sorry, we do not deliver to this location (Distance &gt; 10km).
                                </p>
                            )}

                            {/* Standard Error */}
                            {isDeliverable && orderError && <p className="checkout-error-message">{orderError}</p>}
                            
                            <button 
                                type="submit" 
                                className="place-order-button" 
                                // Disable if not deliverable
                                disabled={isPlacingOrder || authLoading || isCalculating || !isDeliverable}
                                style={!isDeliverable ? { backgroundColor: '#ccc', cursor: 'not-allowed' } : {}}
                            >
                                {isPlacingOrder 
                                    ? 'Processing...' 
                                    : !isDeliverable 
                                        ? 'Location Not Serviceable' 
                                        : `Place Order (${formatPrice(displayTotal, cart.currency, currency)})`
                                }
                            </button>
                        </div>
                    </div>
                </div>
            </form>

             {/* ✅ DYNAMIC SCHEDULE CONFIRMATION MODAL */}
            {showNextDayModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3 className="modal-title">Vendor Currently Unavailable</h3>
                        <p className="modal-message">
                            This vendor is currently closed or on a break.<br /><br />
                            <strong>Would you like to schedule this order for {
                                nextShiftTime ? (
                                    (() => {
                                        const shiftDate = new Date(nextShiftTime);
                                        const isToday = shiftDate.getDate() === new Date().getDate();
                                        const timeStr = shiftDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                        return isToday ? `Later Today at ${timeStr}` : `Tomorrow at ${timeStr}`;
                                    })()
                                ) : "the next available shift"
                            }?</strong><br />
                            <span style={{fontSize: '0.9em', color: '#666'}}>(Delivery ~1 hour after shift starts)</span>
                        </p>

                        <div className="modal-actions">
                            <button
                                className="btn-modal-cancel"
                                onClick={() => setShowNextDayModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-modal-confirm"
                                onClick={handleConfirmNextDay}
                            >
                                Yes, Schedule It
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Checkout;